import { inviteParticipants } from '@/integrations/zoom';
import { useZoom } from '@/providers/ZoomProvider';
import { useState } from 'react';

/**
 * Invites everyone in the Zoom meeting to open the app.
 *
 * Renders nothing unless the running Zoom client actually supports an invitation
 * method. The desktop client supports this for hosts and co-hosts; the Zoom Web App
 * and meeting participants frequently do not, and those users rely on the QR code and
 * shareable link that remain visible alongside this control.
 */
export default function ZoomInviteButton({ className }: { className?: string }) {
  const zoom = useZoom();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'unavailable'>('idle');

  const canInvite =
    zoom.embedded &&
    zoom.inMeeting &&
    (zoom.supports('sendAppInvitationToAllParticipants') ||
      zoom.supports('showAppInvitationDialog'));

  if (!canInvite) return null;

  const handleInvite = async () => {
    setStatus('sending');
    const result = await inviteParticipants();
    setStatus(result === 'unsupported' ? 'unavailable' : 'sent');

    if (result !== 'unsupported') {
      setTimeout(() => setStatus('idle'), 2000);
    }
  };

  const label = {
    idle: 'Invite Meeting Participants',
    sending: 'Inviting...',
    sent: 'Invitation Sent',
    unavailable: 'Use the link below'
  }[status];

  return (
    <button
      type="button"
      className={className ?? 'btn btn-sm btn-neutral'}
      onClick={handleInvite}
      disabled={status === 'sending' || status === 'unavailable'}
    >
      {label}
    </button>
  );
}
